import React from "react";
import { AppHeader } from "./CommunitySelection";
import { useLang } from "../lib/i18n";
import { Zap, CalendarDays, ArrowRight, Clock } from "lucide-react";

const Card = ({ testid, icon: Icon, badge, title, time, desc, cta, onClick }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="group text-left bg-white rounded-lg border border-[var(--line)] p-7 hover:border-vin hover:shadow-md transition-all duration-200"
  >
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-vin/10 text-vin">
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </span>
      <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-vin bg-vin/10 px-3 py-1 rounded-full">
        {badge}
      </span>
    </div>
    <h3 className="mt-5 font-display text-xl font-semibold text-ink group-hover:text-vin transition-colors duration-200">
      {title}
    </h3>
    <div className="mt-1 flex items-center gap-1.5 text-sm text-ink-soft">
      <Clock className="w-3.5 h-3.5" strokeWidth={1.75} /> {time}
    </div>
    <p className="mt-3 text-sm text-ink-soft leading-relaxed">{desc}</p>
    <span className="mt-5 inline-flex items-center gap-2 text-vin font-semibold text-sm">
      {cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" strokeWidth={1.75} />
    </span>
  </button>
);

export const ModeSelect = ({ onSelect }) => {
  const { t } = useLang();
  return (
    <div className="min-h-screen">
      <AppHeader subtitle={t("Choose how to begin")} />
      <div className="max-w-4xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <div className="max-w-2xl animate-rise">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-vin/70 mb-3">
            {t("Choose the depth of your engagement")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            {t("Step into the journey")}
          </h2>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">
            {t("Both paths begin the same way — you will step into the life of a neighbor facing profound challenges. Choose a brief scenario focused on one crucial decision with far-reaching ramifications, or walk a full year to experience how systemic barriers compound over time.")}
          </p>
        </div>

        <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Card
            testid="mode-quick"
            icon={Zap}
            badge={t("Snapshot")}
            title={t("A Single Critical Decision")}
            time={t("About 2–3 minutes · 3 decisions")}
            desc={t("Navigate one pivotal choice and watch its immediate, far-reaching ramifications unfold across a family's stability.")}
            cta={t("Choose this path")}
            onClick={() => onSelect("quick")}
          />
          <Card
            testid="mode-deep"
            icon={CalendarDays}
            badge={t("Full experience")}
            title={t("Walk a Full Year")}
            time={t("About 10–15 minutes · Month by month")}
            desc={t("Journey through an entire year, month by month, to see how everyday trade-offs and systemic barriers compound over time. Concludes with a debrief and reflection.")}
            cta={t("Choose this path")}
            onClick={() => onSelect("deep")}
          />
        </div>
      </div>
    </div>
  );
};

export default ModeSelect;
